import { useCallback, useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { AuthorityConfig, AuthorityWithConfig } from '../interfaces';
import { CONFIG_TYPES } from '../services';
import { deepEqual } from '../utils/deepEqual';
import { deepMerge } from '../utils/deepMerge';
import { getChangedFields, getPreviousValues } from '../utils/getChangedFields';

// Authority-level fields that can be changed (non-config)
interface AuthorityLevelChanges {
  name?: string;
}

type AuthorityWizardChangeRequestType = typeof CONFIG_TYPES.AUTHORITY_METADATA | typeof CONFIG_TYPES.AUTHORITY_CONFIG;

interface AuthorityWizardPendingChanges {
  originalConfig: AuthorityConfig | null;
  accumulatedConfig: Partial<AuthorityConfig>;
  originalAuthorityFields: AuthorityLevelChanges;
  accumulatedAuthorityFields: AuthorityLevelChanges;
  submittedChangeRequestTypes: AuthorityWizardChangeRequestType[];
}

const QUERY_KEY_BASE = 'authorityWizardPendingChanges';
const NEW_AUTHORITY_KEY = 'new-authority';

const getQueryKey = (authorityUuid: string) => [QUERY_KEY_BASE, authorityUuid];

const hasAccumulatedChanges = (pending: AuthorityWizardPendingChanges | null | undefined): boolean =>
  Object.keys(pending?.accumulatedConfig ?? {}).length > 0 || Object.keys(pending?.accumulatedAuthorityFields ?? {}).length > 0;

/**
 * Helper to preserve previous state while updating specific fields.
 * Provides defaults for all fields to ensure type safety.
 */
const updateState = (
  prev: AuthorityWizardPendingChanges | undefined,
  updates: Partial<AuthorityWizardPendingChanges>,
): AuthorityWizardPendingChanges => ({
  originalConfig: prev?.originalConfig ?? null,
  accumulatedConfig: prev?.accumulatedConfig ?? {},
  originalAuthorityFields: prev?.originalAuthorityFields ?? {},
  accumulatedAuthorityFields: prev?.accumulatedAuthorityFields ?? {},
  submittedChangeRequestTypes: prev?.submittedChangeRequestTypes ?? [],
  ...updates,
});

/**
 * Hook to manage pending changes during Authority Wizard.
 *
 * Supports TWO modes:
 * 1. EDIT MODE: When editing an existing authority (authority_uuid exists from API)
 *    - Changes are accumulated in React Query state
 *    - Config and metadata changes go through separate Change Requests in Step 4
 *
 * 2. CREATION MODE: When creating a new authority (no authority_uuid)
 *    - ALL changes are accumulated in state
 *    - Authority is created in Step 4 with all accumulated data
 *    - Uses a special "new-authority" key for state storage
 *
 * @param authority - Authority object (may or may not have uuid from API)
 */
export function useAuthorityWizardPendingChanges(authority: Partial<AuthorityWithConfig> | null) {
  // Edit mode is determined by whether authority has a uuid from the API
  const isEditMode = !!authority?.authority_uuid;
  const isCreationMode = !authority?.authority_uuid;

  // For edit mode, use authority_uuid; for creation mode, use special "new-authority" key
  const effectiveKey = authority?.authority_uuid || NEW_AUTHORITY_KEY;
  const queryKey = useMemo(() => getQueryKey(effectiveKey), [effectiveKey]);

  /**
   * Get current pending changes from React Query state
   */
  const getPendingChanges = useCallback((): AuthorityWizardPendingChanges | null => {
    return queryClient.getQueryData<AuthorityWizardPendingChanges>(queryKey) ?? null;
  }, [queryKey]);

  /**
   * Add config changes from a wizard step.
   * Changes are deep merged with existing accumulated changes.
   * @param configChanges - The config changes to add
   * @param explicitOriginalConfig - Optional: explicitly pass the original config
   */
  const addConfigChanges = (configChanges: Partial<AuthorityConfig>, explicitOriginalConfig?: AuthorityConfig | null) => {
    queryClient.setQueryData<AuthorityWizardPendingChanges>(queryKey, (prev) => {
      // Preserve original config on first write
      const hasExistingOriginalConfig = prev?.originalConfig && Object.keys(prev.originalConfig).length > 0;
      const originalConfig = hasExistingOriginalConfig
        ? prev.originalConfig
        : (explicitOriginalConfig ?? (authority?.config as AuthorityConfig) ?? null);

      // Deep merge new changes with accumulated
      const newAccumulated = deepMerge(prev?.accumulatedConfig ?? {}, configChanges) as Partial<AuthorityConfig>;
      const hasNewConfigChanges = !deepEqual(newAccumulated, prev?.accumulatedConfig ?? {});

      return updateState(prev, {
        originalConfig,
        accumulatedConfig: newAccumulated,
        submittedChangeRequestTypes: hasNewConfigChanges
          ? (prev?.submittedChangeRequestTypes ?? []).filter((type) => type !== CONFIG_TYPES.AUTHORITY_CONFIG)
          : prev?.submittedChangeRequestTypes,
        // Preserve authority fields
        originalAuthorityFields: prev?.originalAuthorityFields ?? { name: authority?.name },
      });
    });
  };

  /**
   * Add authority-level field changes from a wizard step.
   * These are non-config fields like name.
   */
  const addAuthorityChanges = (authorityChanges: AuthorityLevelChanges) => {
    queryClient.setQueryData<AuthorityWizardPendingChanges>(queryKey, (prev) => {
      // Capture original authority fields if not already captured
      const originalAuthorityFields = prev?.originalAuthorityFields ?? { name: authority?.name };
      const accumulatedAuthorityFields = {
        ...prev?.accumulatedAuthorityFields,
        ...authorityChanges,
      };
      const hasNewMetadataChanges = !deepEqual(accumulatedAuthorityFields, prev?.accumulatedAuthorityFields ?? {});

      return updateState(prev, {
        originalConfig: prev?.originalConfig ?? (authority?.config as AuthorityConfig) ?? null,
        originalAuthorityFields,
        accumulatedAuthorityFields,
        submittedChangeRequestTypes: hasNewMetadataChanges
          ? (prev?.submittedChangeRequestTypes ?? []).filter((type) => type !== CONFIG_TYPES.AUTHORITY_METADATA)
          : prev?.submittedChangeRequestTypes,
      });
    });
  };

  /**
   * Get the final merged config (original + accumulated changes).
   * This represents what the config would look like after all changes are applied.
   */
  const getMergedConfig = (): AuthorityConfig | null => {
    const pending = getPendingChanges();

    // For creation mode, just return accumulated config
    if (isCreationMode) {
      return pending?.accumulatedConfig as AuthorityConfig | null;
    }

    if (!pending?.originalConfig) {
      return null;
    }

    return deepMerge(
      pending.originalConfig as unknown as Record<string, unknown>,
      pending.accumulatedConfig as unknown as Record<string, unknown>,
    ) as unknown as AuthorityConfig;
  };

  /**
   * Get the diff between original and accumulated changes.
   * Used when creating the Change Request.
   */
  const getChangesDiff = () => {
    const pending = getPendingChanges();

    if (!pending?.originalConfig) {
      return { changedFields: {}, previousValues: {} };
    }

    const mergedConfig = deepMerge(
      pending.originalConfig as unknown as Record<string, unknown>,
      pending.accumulatedConfig as unknown as Record<string, unknown>,
    ) as Record<string, unknown>;
    // No includeDeletions=true here: deepMerge in the wizard path can never remove keys,
    // so deletions are structurally impossible and the flag is not needed.
    const changedFields = getChangedFields(pending.originalConfig as unknown as Record<string, unknown>, mergedConfig);
    const previousValues = getPreviousValues(pending.originalConfig as unknown as Record<string, unknown>, changedFields);

    return { changedFields, previousValues };
  };

  /**
   * Check if there are any pending config changes that differ from the original.
   * Uses getChangesDiff to detect ACTUAL changes, not just accumulated values.
   */
  const hasPendingConfigChanges = (): boolean => {
    const { changedFields } = getChangesDiff();

    return Object.keys(changedFields).length > 0;
  };

  /**
   * Check if there are any pending changes (config or authority-level)
   */
  const hasPendingChanges = useCallback((): boolean => {
    return hasAccumulatedChanges(getPendingChanges());
  }, [getPendingChanges]);

  /**
   * Get the authority-level metadata fields that have changed.
   * Returns only the fields that differ from the original values.
   */
  const getAuthorityFieldsToSave = (): AuthorityLevelChanges => {
    const pending = getPendingChanges();

    if (!pending?.accumulatedAuthorityFields) {
      return {};
    }

    const result: AuthorityLevelChanges = {};
    const accumulated = pending.accumulatedAuthorityFields;
    const original = pending.originalAuthorityFields;

    // Only include fields that actually changed
    if (accumulated.name !== undefined && accumulated.name !== original.name) {
      result.name = accumulated.name;
    }

    return result;
  };

  const hasPendingMetadataChanges = (): boolean => {
    return Object.keys(getAuthorityFieldsToSave()).length > 0;
  };

  const markChangeRequestSubmitted = (configType: AuthorityWizardChangeRequestType) => {
    const updateSubmittedChangeRequestTypes = (previousState: AuthorityWizardPendingChanges | undefined) => {
      const currentState = updateState(previousState, {});
      const { submittedChangeRequestTypes } = currentState;
      const isAlreadySubmitted = submittedChangeRequestTypes.includes(configType);

      if (isAlreadySubmitted) {
        return currentState;
      }

      const updatedSubmittedChangeRequestTypes = [...submittedChangeRequestTypes, configType];

      return {
        ...currentState,
        submittedChangeRequestTypes: updatedSubmittedChangeRequestTypes,
      };
    };

    queryClient.setQueryData<AuthorityWizardPendingChanges>(queryKey, updateSubmittedChangeRequestTypes);
  };

  const hasSubmittedChangeRequest = (configType: AuthorityWizardChangeRequestType): boolean => {
    const pendingChanges = getPendingChanges();
    const submittedChangeRequestTypes = pendingChanges?.submittedChangeRequestTypes ?? [];

    return submittedChangeRequestTypes.includes(configType);
  };

  /**
   * Clear all pending changes (e.g., when wizard is closed or CR is created)
   */
  const clearPendingChanges = () => {
    queryClient.removeQueries({ queryKey });
  };

  return {
    isEditMode,
    isCreationMode,
    addConfigChanges,
    addAuthorityChanges,
    getMergedConfig,
    getChangesDiff,
    hasPendingChanges,
    hasPendingConfigChanges,
    hasPendingMetadataChanges,
    getAuthorityFieldsToSave,
    markChangeRequestSubmitted,
    hasSubmittedChangeRequest,
    clearPendingChanges,
    getPendingChanges,
  };
}

/**
 * Clear pending changes for a specific authority.
 * Can be called from outside React components.
 */
export function clearAuthorityWizardPendingChanges(authorityUuid: string) {
  queryClient.removeQueries({ queryKey: getQueryKey(authorityUuid) });
}
